import React, { useState, useEffect } from 'react'
import {
  Tabs,
  Table,
  Card,
  Row,
  Col,
  Statistic,
  Space,
  Tag,
  Button,
  message,
  Spin,
  Empty,
  Select,
  Modal,
  Tooltip,
  Typography,
  Alert,
  Progress,
  Divider,
} from 'antd'
import {
  ReloadOutlined,
  RobotOutlined,
  LineChartOutlined,
  HistoryOutlined,
  DashboardOutlined,
  EyeOutlined,
  RiseOutlined,
  FallOutlined,
  SwapOutlined,
  AreaChartOutlined,
  ClockCircleOutlined,
  SyncOutlined,
  UserOutlined,
  StockOutlined,
  UnorderedListOutlined,
  DollarOutlined,
  ThunderboltOutlined,
  TrophyOutlined,
  BankOutlined,
  RocketOutlined,
  FundOutlined,
} from '@ant-design/icons'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer, AreaChart, Area, ComposedChart, Bar } from 'recharts'
import { masterPortfoliosAPI, aiPortfoliosAPI } from '../services/api'

const { TabPane } = Tabs
const { Title, Text } = Typography

// Helper to parse date string without timezone shift (YYYY-MM-DD -> local date)
const formatDateString = (dateStr) => {
  if (!dateStr) return '-'
  // Handle ISO date strings that might have time component
  const datePart = dateStr.split('T')[0]
  const [year, month, day] = datePart.split('-').map(Number)
  if (!year || !month || !day) return '-'
  return new Date(year, month - 1, day).toLocaleDateString()
}

const PortfoliosEnhanced = () => {
  const [activeTab, setActiveTab] = useState('master-portfolios')
  const [loading, setLoading] = useState(false)

  // Master Portfolios state (continuous portfolio management)
  const [masterPortfolios, setMasterPortfolios] = useState([])
  const [masterPortfolioStats, setMasterPortfolioStats] = useState(null)
  const [selectedMasterPortfolio, setSelectedMasterPortfolio] = useState(null)
  const [masterPositionChanges, setMasterPositionChanges] = useState([])
  const [masterDailyValues, setMasterDailyValues] = useState([])
  const [masterEvaluations, setMasterEvaluations] = useState([])
  const [masterDetailsVisible, setMasterDetailsVisible] = useState(false)
  const [chartDays, setChartDays] = useState(30)

  // Position History state
  const [positionHistory, setPositionHistory] = useState([])
  const [positionHistoryFilter, setPositionHistoryFilter] = useState({ strategy_type: null })

  useEffect(() => {
    loadMasterPortfolioData()
  }, [])

  useEffect(() => {
    if (activeTab === 'master-portfolios') {
      loadMasterPortfolioData()
    } else if (activeTab === 'position-history') {
      loadPositionHistoryData()
    }
  }, [activeTab, positionHistoryFilter])

  // Load master portfolio data
  const loadMasterPortfolioData = async () => {
    try {
      setLoading(true)

      // Refresh prices from Polygon API first (same pattern as client portal)
      try {
        await aiPortfoliosAPI.refreshPrices()
      } catch (refreshError) {
        console.warn('Could not refresh prices from Polygon:', refreshError)
        // Continue loading - will use cached prices
      }

      // Load all master portfolio data in parallel
      const [portfoliosRes, statsRes, evaluationsRes] = await Promise.all([
        masterPortfoliosAPI.list(),
        masterPortfoliosAPI.getStats().catch(() => null),  // Stats failure is non-critical
        masterPortfoliosAPI.getRecentEvaluations().catch(() => ({ evaluations: [] })),  // Non-critical
      ])

      // API returns arrays directly, not wrapped in objects
      setMasterPortfolios(Array.isArray(portfoliosRes) ? portfoliosRes : (portfoliosRes?.portfolios || []))
      setMasterPortfolioStats(statsRes)
      setMasterEvaluations(Array.isArray(evaluationsRes) ? evaluationsRes : (evaluationsRes?.evaluations || []))
    } catch (error) {
      console.error('Error loading master portfolio data:', error)
      message.error('Failed to load master portfolio data')
    } finally {
      setLoading(false)
    }
  }

  // Load position history for superseded positions
  const loadPositionHistoryData = async () => {
    try {
      setLoading(true)
      const params = { limit: 50 }
      if (positionHistoryFilter.strategy_type) {
        params.strategy_type = positionHistoryFilter.strategy_type
      }

      // Use the all position changes endpoint (supports optional strategy filter)
      const data = await masterPortfoliosAPI.getAllPositionChanges(params)
      // API returns array directly, not { changes: [...] }
      setPositionHistory(Array.isArray(data) ? data : (data.changes || []))
    } catch (error) {
      console.error('Error loading position history:', error)
    } finally {
      setLoading(false)
    }
  }

  // View master portfolio details
  const handleViewMasterPortfolio = async (portfolio) => {
    try {
      setLoading(true)

      // Load detailed data for this portfolio
      const [detailRes, changesRes, valuesRes] = await Promise.all([
        masterPortfoliosAPI.get(portfolio.strategy_type),
        masterPortfoliosAPI.getPositionChanges(portfolio.strategy_type, 20),
        masterPortfoliosAPI.getDailyValues(portfolio.strategy_type, chartDays),
      ])

      setSelectedMasterPortfolio(detailRes)
      // API returns array directly, not { changes: [...] }
      setMasterPositionChanges(Array.isArray(changesRes) ? changesRes : (changesRes.changes || []))
      setMasterDailyValues(valuesRes.daily_values || [])
      setMasterDetailsVisible(true)
    } catch (error) {
      console.error('Error loading master portfolio details:', error)
      message.error('Failed to load portfolio details')
    } finally {
      setLoading(false)
    }
  }

  // Trigger manual evaluation
  const handleTriggerEvaluation = async () => {
    try {
      message.loading({ content: 'Running portfolio evaluation...', key: 'eval', duration: 0 })
      // This would call a backend endpoint to trigger the daily evaluation
      // For now just show a message
      message.info({ content: 'Portfolio evaluation runs automatically at 5:30 PM PT after market close', key: 'eval' })
    } catch (error) {
      message.error({ content: 'Failed to trigger evaluation', key: 'eval' })
    }
  }

  // Calculate price-based return for a position
  const calculatePositionReturn = (position) => {
    if (!position.original_price || !position.current_price) return null
    return ((position.current_price - position.original_price) / position.original_price) * 100
  }

  // Calculate weighted portfolio return from positions (dynamic based on current prices)
  const calculatePortfolioReturn = (portfolio) => {
    const positions = portfolio?.positions || []
    if (positions.length === 0) return null

    let totalWeightedReturn = 0
    let totalWeight = 0

    for (const pos of positions) {
      const originalPrice = pos.original_price || pos.price
      const currentPrice = pos.current_price
      const weight = pos.weight || 0

      if (originalPrice && currentPrice && weight > 0) {
        const posReturn = (currentPrice - originalPrice) / originalPrice
        totalWeightedReturn += posReturn * weight
        totalWeight += weight
      }
    }

    // Return weighted average return (or null if no valid positions)
    if (totalWeight === 0) return null
    return (totalWeightedReturn / totalWeight) * 100
  }

  // Master portfolio columns
  const masterPortfolioColumns = [
    {
      title: 'Strategy',
      dataIndex: 'display_name',
      key: 'display_name',
      render: (text, record) => (
        <div>
          <div className="font-semibold">{text}</div>
          <div className="text-xs text-gray-500">{record.strategy_type}</div>
        </div>
      ),
    },
    {
      title: 'Positions',
      dataIndex: 'position_count',
      key: 'position_count',
      align: 'center',
    },
    {
      title: 'Portfolio Return',
      key: 'portfolio_return',
      align: 'right',
      render: (_, record) => {
        const dynamicReturn = calculatePortfolioReturn(record)
        if (dynamicReturn === null) {
          // Fallback to stored value if positions don't have current prices
          const val = record.total_return_pct
          if (val == null) return '-'
          return (
            <span style={{ color: val >= 0 ? '#52c41a' : '#ff4d4f', fontWeight: 'bold' }}>
              {val >= 0 ? '+' : ''}{val?.toFixed(2)}%
            </span>
          )
        }
        return (
          <Tooltip title="Calculated from current prices (live)">
            <span style={{ color: dynamicReturn >= 0 ? '#52c41a' : '#ff4d4f', fontWeight: 'bold' }}>
              {dynamicReturn >= 0 ? '+' : ''}{dynamicReturn.toFixed(2)}%
              <SyncOutlined style={{ marginLeft: 4, fontSize: 10, color: '#1890ff' }} />
            </span>
          </Tooltip>
        )
      },
    },
    {
      title: 'Risk Level',
      dataIndex: 'risk_level',
      key: 'risk_level',
      render: (level) => (
        <Tag color={level === 'low' ? 'green' : level === 'medium' ? 'orange' : 'red'}>
          {level?.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Position Changes',
      dataIndex: 'total_position_changes',
      key: 'total_position_changes',
      align: 'center',
    },
    {
      title: 'Last Evaluated',
      dataIndex: 'last_evaluated_at',
      key: 'last_evaluated_at',
      render: (date) => date ? formatDateString(date) : 'Never',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Button
          type="primary"
          icon={<EyeOutlined />}
          onClick={() => handleViewMasterPortfolio(record)}
        >
          View Details
        </Button>
      ),
    },
  ]

  // Position columns for detail view (price-based returns)
  // Sorted by ML score - top AI picks first
  const positionColumns = [
    {
      title: 'Ticker',
      dataIndex: 'ticker',
      key: 'ticker',
      width: 140,
      render: (ticker, record) => (
        <div>
          <a href={`https://finance.yahoo.com/quote/${ticker}`} target="_blank" rel="noopener noreferrer">
            <strong>{ticker}</strong>
          </a>
          <div className="text-xs text-gray-500 truncate" style={{ maxWidth: 120 }}>{record.company_name}</div>
        </div>
      ),
    },
    {
      title: 'ML Score',
      dataIndex: 'ml_score',
      key: 'ml_score',
      width: 90,
      align: 'center',
      render: (score) => (
        <Progress
          type="circle"
          percent={Math.round((score || 0) * 100)}
          width={40}
          strokeColor={score >= 0.7 ? '#52c41a' : score >= 0.4 ? '#faad14' : '#ff4d4f'}
          format={(p) => `${p}`}
        />
      ),
    },
    {
      title: 'Entry Price',
      dataIndex: 'original_price',
      key: 'original_price',
      width: 100,
      align: 'right',
      render: (p) => p ? `$${p.toFixed(2)}` : '-',
    },
    {
      title: 'Current Price',
      dataIndex: 'current_price',
      key: 'current_price',
      width: 100,
      align: 'right',
      render: (p) => p ? `$${p.toFixed(2)}` : '-',
    },
    {
      title: 'Return',
      key: 'return',
      width: 100,
      align: 'right',
      render: (_, record) => {
        const ret = calculatePositionReturn(record)
        if (ret === null) return '-'
        return (
          <span style={{ color: ret >= 0 ? '#52c41a' : '#ff4d4f', fontWeight: 'bold' }}>
            {ret >= 0 ? <RiseOutlined /> : <FallOutlined />} {ret >= 0 ? '+' : ''}{ret.toFixed(2)}%
          </span>
        )
      },
    },
    {
      title: 'Added',
      dataIndex: 'added_date',
      key: 'added_date',
      width: 100,
      render: (date) => formatDateString(date),
    },
    {
      title: 'Sector',
      dataIndex: 'sector',
      key: 'sector',
      width: 180,
      ellipsis: true,
      render: (sector) => <Tag>{sector || 'Unknown'}</Tag>,
    },
  ]

  // Evaluation columns
  const evaluationColumns = [
    {
      title: 'Date',
      dataIndex: 'evaluation_date',
      key: 'evaluation_date',
      render: (date) => formatDateString(date),
    },
    {
      title: 'Strategy',
      dataIndex: 'strategy_type',
      key: 'strategy_type',
      render: (s) => <Tag color={s?.includes('dividend') ? 'blue' : 'purple'}>{s}</Tag>,
    },
    {
      title: 'Positions Evaluated',
      dataIndex: 'positions_evaluated',
      key: 'positions_evaluated',
      align: 'center',
    },
    {
      title: 'Flagged',
      dataIndex: 'stale_positions_found',
      key: 'stale_positions_found',
      align: 'center',
      render: (val) => val > 0 ? <Tag color="orange">{val}</Tag> : <Tag>0</Tag>,
    },
    {
      title: 'Replaced',
      dataIndex: 'replacements_made',
      key: 'replacements_made',
      align: 'center',
      render: (val) => val > 0 ? <Tag color="green">{val}</Tag> : <Tag>0</Tag>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === 'completed' ? 'green' : status === 'running' ? 'blue' : 'red'}>
          {status?.toUpperCase()}
        </Tag>
      ),
    },
  ]

  // Position changes columns (for modal - matches positionHistoryColumns format)
  const positionChangesColumns = [
    {
      title: 'Date',
      dataIndex: 'change_date',
      key: 'change_date',
      width: 100,
      render: (date) => formatDateString(date),
    },
    {
      title: 'Action',
      dataIndex: 'change_type',
      key: 'change_type',
      width: 80,
      render: (type) => (
        <Tag color={type === 'ADD' ? 'green' : type === 'REMOVE' ? 'red' : 'orange'}>
          {type}
        </Tag>
      ),
    },
    {
      title: 'Ticker',
      key: 'ticker',
      width: 140,
      render: (_, record) => {
        // For replacements, show old → new
        if (record.change_type === 'replace' && record.old_ticker && record.new_ticker) {
          return (
            <div style={{ fontSize: '12px' }}>
              <a href={`https://finance.yahoo.com/quote/${record.old_ticker}`} target="_blank" rel="noopener noreferrer">
                <span style={{ color: '#ff4d4f' }}>{record.old_ticker}</span>
              </a>
              <span style={{ margin: '0 4px' }}>→</span>
              <a href={`https://finance.yahoo.com/quote/${record.new_ticker}`} target="_blank" rel="noopener noreferrer">
                <span style={{ color: '#52c41a', fontWeight: 600 }}>{record.new_ticker}</span>
              </a>
            </div>
          )
        }
        // For other change types, show single ticker
        return (
          <div>
            <a href={`https://finance.yahoo.com/quote/${record.ticker}`} target="_blank" rel="noopener noreferrer">
              <strong>{record.ticker}</strong>
            </a>
            {record.company_name && record.company_name !== record.ticker && (
              <div className="text-xs text-gray-500" style={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {record.company_name}
              </div>
            )}
          </div>
        )
      },
    },
    {
      title: 'Entry',
      key: 'entry',
      align: 'right',
      width: 100,
      render: (_, record) => {
        if (record.change_type === 'REMOVE' && record.entry_price) {
          return (
            <div>
              <div>${record.entry_price.toFixed(2)}</div>
              {record.entry_date && (
                <div className="text-xs text-gray-500">{formatDateString(record.entry_date)}</div>
              )}
            </div>
          )
        }
        if (record.change_type === 'ADD' && record.new_price) {
          return <div>${record.new_price.toFixed(2)}</div>
        }
        return '-'
      },
    },
    {
      title: 'Exit',
      key: 'exit',
      align: 'right',
      width: 90,
      render: (_, record) => {
        if (record.change_type === 'REMOVE' && record.exit_price) {
          return <div>${record.exit_price.toFixed(2)}</div>
        }
        return '-'
      },
    },
    {
      title: 'Return',
      key: 'return',
      align: 'right',
      width: 80,
      render: (_, record) => {
        if (record.change_type === 'REMOVE' && record.return_pct != null) {
          const pct = record.return_pct * 100
          return (
            <span style={{ color: pct >= 0 ? '#52c41a' : '#ff4d4f', fontWeight: 600 }}>
              {pct >= 0 ? '+' : ''}{pct.toFixed(1)}%
            </span>
          )
        }
        return '-'
      },
    },
    {
      title: 'Days',
      key: 'hold_days',
      align: 'right',
      width: 60,
      render: (_, record) => {
        if (record.change_type === 'REMOVE' && record.hold_days != null) {
          return `${record.hold_days}d`
        }
        return '-'
      },
    },
    {
      title: 'ML',
      key: 'ml_score',
      align: 'right',
      width: 60,
      render: (_, record) => {
        const oldScore = record.ml_score_old
        const newScore = record.ml_score_new
        if (record.change_type === 'REMOVE' && oldScore != null) {
          return <span style={{ color: '#ff4d4f' }}>{(oldScore * 100).toFixed(0)}%</span>
        }
        if (record.change_type === 'ADD' && newScore != null) {
          return <span style={{ color: '#52c41a' }}>{(newScore * 100).toFixed(0)}%</span>
        }
        return '-'
      },
    },
    {
      title: 'Reason',
      dataIndex: 'reason',
      key: 'reason',
      render: (reason) => reason?.replace(/_/g, ' ') || '-',
    },
  ]

  // Position history columns (for position changes)
  const positionHistoryColumns = [
    {
      title: 'Date',
      dataIndex: 'change_date',
      key: 'change_date',
      width: 100,
      render: (date) => formatDateString(date),
    },
    {
      title: 'Action',
      dataIndex: 'change_type',
      key: 'change_type',
      width: 80,
      render: (type) => (
        <Tag color={type === 'ADD' ? 'green' : type === 'REMOVE' ? 'red' : 'orange'}>
          {type}
        </Tag>
      ),
    },
    {
      title: 'Ticker',
      key: 'ticker',
      width: 140,
      render: (_, record) => {
        // For replacements, show old → new
        if (record.change_type === 'replace' && record.old_ticker && record.new_ticker) {
          return (
            <div style={{ fontSize: '12px' }}>
              <a href={`https://finance.yahoo.com/quote/${record.old_ticker}`} target="_blank" rel="noopener noreferrer">
                <span style={{ color: '#ff4d4f' }}>{record.old_ticker}</span>
              </a>
              <span style={{ margin: '0 4px' }}>→</span>
              <a href={`https://finance.yahoo.com/quote/${record.new_ticker}`} target="_blank" rel="noopener noreferrer">
                <span style={{ color: '#52c41a', fontWeight: 600 }}>{record.new_ticker}</span>
              </a>
            </div>
          )
        }
        // For other change types, show single ticker
        return (
          <div>
            <a href={`https://finance.yahoo.com/quote/${record.ticker}`} target="_blank" rel="noopener noreferrer">
              <strong>{record.ticker}</strong>
            </a>
            {record.company_name && record.company_name !== record.ticker && (
              <div className="text-xs text-gray-500" style={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {record.company_name}
              </div>
            )}
          </div>
        )
      },
    },
    {
      title: 'Entry',
      key: 'entry',
      align: 'right',
      width: 100,
      render: (_, record) => {
        // REPLACE records have exit data for the old (replaced) position
        if (record.change_type === 'REPLACE' && record.entry_price) {
          return (
            <div>
              <div>${record.entry_price.toFixed(2)}</div>
              {record.entry_date && (
                <div className="text-xs text-gray-500">{formatDateString(record.entry_date)}</div>
              )}
            </div>
          )
        }
        if ((record.change_type === 'ADD' || record.change_type === 'ADDITION') && record.new_price) {
          return <div>${record.new_price.toFixed(2)}</div>
        }
        return '-'
      },
    },
    {
      title: 'Exit',
      key: 'exit',
      align: 'right',
      width: 90,
      render: (_, record) => {
        if (record.change_type === 'REPLACE' && record.exit_price) {
          return <div>${record.exit_price.toFixed(2)}</div>
        }
        return '-'
      },
    },
    {
      title: 'Return',
      key: 'return',
      align: 'right',
      width: 80,
      render: (_, record) => {
        if (record.change_type === 'REPLACE' && record.return_pct != null) {
          // return_pct is already stored as percentage (e.g., -2.96 for -2.96%)
          const pct = record.return_pct
          return (
            <span style={{ color: pct >= 0 ? '#52c41a' : '#ff4d4f', fontWeight: 600 }}>
              {pct >= 0 ? '+' : ''}{pct.toFixed(1)}%
            </span>
          )
        }
        return '-'
      },
    },
    {
      title: 'Days Held',
      key: 'hold_days',
      align: 'right',
      width: 80,
      render: (_, record) => {
        if (record.change_type === 'REPLACE' && record.hold_days != null) {
          return `${record.hold_days}d`
        }
        return '-'
      },
    },
    {
      title: 'ML Score',
      key: 'ml_score',
      align: 'right',
      width: 80,
      render: (_, record) => {
        const oldScore = record.ml_score_old
        const newScore = record.ml_score_new
        if (record.change_type === 'REPLACE' && oldScore != null) {
          return <span style={{ color: '#ff4d4f' }}>{(oldScore * 100).toFixed(0)}%</span>
        }
        if ((record.change_type === 'ADD' || record.change_type === 'ADDITION') && newScore != null) {
          return <span style={{ color: '#52c41a' }}>{(newScore * 100).toFixed(0)}%</span>
        }
        return '-'
      },
    },
    {
      title: 'Reason',
      dataIndex: 'reason',
      key: 'reason',
      render: (reason) => reason?.replace(/_/g, ' ') || '-',
    },
  ]

  return (
    <div className="p-6">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">AI Portfolio Management</h1>
          <p className="text-gray-600">Continuous portfolio management with surgical daily updates</p>
        </div>
        <Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={loadMasterPortfolioData}
            loading={loading}
          >
            Refresh
          </Button>
        </Space>
      </div>

      <Tabs activeKey={activeTab} onChange={setActiveTab} size="large">
        {/* MASTER PORTFOLIOS - Main Tab */}
        <TabPane
          tab={
            <span>
              <RobotOutlined />
              Master Portfolios
            </span>
          }
          key="master-portfolios"
        >
          <Spin spinning={loading}>
            <div className="space-y-6">
              {/* Info Alert */}
              <Alert
                message="Continuous Portfolio Management"
                description="Master portfolios are long-lived portfolios with surgical daily updates (max 2 positions/day). Returns are calculated as percentage gains from entry price - no dollar amounts are tracked."
                type="info"
                showIcon
                icon={<RobotOutlined />}
              />

              {/* Overall Stats */}
              {masterPortfolioStats && (
                <Row gutter={16}>
                  <Col span={6}>
                    <Card>
                      <Statistic
                        title="Active Strategies"
                        value={masterPortfolioStats.portfolios?.total || 0}
                        prefix={<StockOutlined />}
                      />
                    </Card>
                  </Col>
                  <Col span={6}>
                    <Card>
                      <Statistic
                        title="Total Positions"
                        value={masterPortfolioStats.portfolios?.total_positions || 0}
                      />
                    </Card>
                  </Col>
                  <Col span={6}>
                    <Card>
                      {(() => {
                        // Calculate dynamic average return from all portfolios
                        let avgReturn = masterPortfolioStats.portfolios?.avg_return_pct || 0
                        let isLive = false
                        if (masterPortfolios.length > 0) {
                          const returns = masterPortfolios
                            .map(p => calculatePortfolioReturn(p))
                            .filter(r => r !== null)
                          if (returns.length > 0) {
                            avgReturn = returns.reduce((a, b) => a + b, 0) / returns.length
                            isLive = true
                          }
                        }
                        return (
                          <Statistic
                            title={<span>Avg Return {isLive && <SyncOutlined style={{ fontSize: 10, color: '#1890ff' }} />}</span>}
                            value={avgReturn}
                            precision={2}
                            suffix="%"
                            valueStyle={{ color: avgReturn >= 0 ? '#52c41a' : '#ff4d4f' }}
                            prefix={avgReturn >= 0 ? <RiseOutlined /> : <FallOutlined />}
                          />
                        )
                      })()}
                    </Card>
                  </Col>
                  <Col span={6}>
                    <Card>
                      <Statistic
                        title="Position Changes (7d)"
                        value={masterPortfolioStats.activity?.changes_last_7_days || 0}
                        prefix={<SwapOutlined />}
                      />
                    </Card>
                  </Col>
                </Row>
              )}


              {/* Master Portfolios Table */}
              <Card title="Master Portfolios">
                {masterPortfolios.length > 0 ? (
                  <Table
                    columns={masterPortfolioColumns}
                    dataSource={masterPortfolios}
                    rowKey="strategy_type"
                    pagination={false}
                  />
                ) : (
                  <Empty description="No master portfolios found. Master portfolios are created during system setup." />
                )}
              </Card>

              {/* Recent Evaluations */}
              <Card title="Recent Position Evaluations" extra={
                <Text type="secondary">Daily evaluations run at 5:30 PM PT</Text>
              }>
                {masterEvaluations.length > 0 ? (
                  <Table
                    columns={evaluationColumns}
                    dataSource={masterEvaluations}
                    rowKey="evaluation_id"
                    pagination={{ pageSize: 10 }}
                    size="small"
                  />
                ) : (
                  <Empty description="No evaluations recorded yet" />
                )}
              </Card>

            </div>
          </Spin>
        </TabPane>

        {/* POSITION HISTORY - Track superseded positions */}
        <TabPane
          tab={
            <span>
              <HistoryOutlined />
              Position History
            </span>
          }
          key="position-history"
        >
          <Spin spinning={loading}>
            <Card title="Position Change History">
              <Alert
                message="Historical Performance Tracking"
                description="Track all position changes with entry/exit prices and percentage returns. Use strategy tabs to filter, or view all changes combined."
                type="info"
                className="mb-4"
              />
              <Tabs
                activeKey={positionHistoryFilter.strategy_type || 'all'}
                onChange={(key) => setPositionHistoryFilter({ ...positionHistoryFilter, strategy_type: key === 'all' ? null : key })}
                items={[
                  {
                    key: 'all',
                    label: <span><UnorderedListOutlined /> All</span>,
                  },
                  {
                    key: 'dividend_strategy',
                    label: <span><DollarOutlined /> Dividend</span>,
                  },
                  {
                    key: 'adaptive_strategy',
                    label: <span><ThunderboltOutlined /> Adaptive</span>,
                  },
                  {
                    key: 'value_strategy',
                    label: <span><BankOutlined /> Value</span>,
                  },
                  {
                    key: 'growth_strategy',
                    label: <span><RocketOutlined /> Growth</span>,
                  },
                  {
                    key: 'value_largecap_strategy',
                    label: <span><BankOutlined /> Value LC</span>,
                  },
                  {
                    key: 'growth_largecap_strategy',
                    label: <span><RocketOutlined /> Growth LC</span>,
                  },
                  {
                    key: 'value_smallcap_strategy',
                    label: <span><FundOutlined /> Value SC</span>,
                  },
                  {
                    key: 'growth_smallcap_strategy',
                    label: <span><TrophyOutlined /> Growth SC</span>,
                  },
                ]}
              />
              {positionHistory.length > 0 ? (
                <Table
                  columns={positionHistoryColumns}
                  dataSource={positionHistory}
                  rowKey="change_id"
                  pagination={{ pageSize: 20 }}
                  scroll={{ x: 900 }}
                  size="small"
                />
              ) : (
                <Empty description="No position changes recorded yet" />
              )}
            </Card>
          </Spin>
        </TabPane>
      </Tabs>

      {/* Master Portfolio Details Modal */}
      <Modal
        title={
          <div>
            <RobotOutlined className="mr-2" />
            {selectedMasterPortfolio?.display_name || 'Portfolio Details'}
          </div>
        }
        open={masterDetailsVisible}
        onCancel={() => setMasterDetailsVisible(false)}
        width={1400}
        footer={null}
      >
        {selectedMasterPortfolio && (
          <div className="space-y-6">
            {/* Summary Stats */}
            <Row gutter={16}>
              <Col span={6}>
                <Statistic
                  title="Positions"
                  value={selectedMasterPortfolio.position_count}
                />
              </Col>
              <Col span={6}>
                {(() => {
                  const dynamicReturn = calculatePortfolioReturn(selectedMasterPortfolio)
                  const returnValue = dynamicReturn !== null
                    ? dynamicReturn
                    : (selectedMasterPortfolio.total_return_pct || 0)
                  return (
                    <Statistic
                      title={<span>Total Return {dynamicReturn !== null && <SyncOutlined style={{ fontSize: 10, color: '#1890ff' }} />}</span>}
                      value={returnValue.toFixed(2)}
                      suffix="%"
                      valueStyle={{ color: returnValue >= 0 ? '#52c41a' : '#ff4d4f' }}
                    />
                  )
                })()}
              </Col>
              <Col span={6}>
                <Statistic
                  title="Position Changes"
                  value={selectedMasterPortfolio.total_position_changes}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="Evaluations"
                  value={selectedMasterPortfolio.total_evaluations}
                />
              </Col>
            </Row>

            {/* Performance Chart */}
            {masterDailyValues.length > 0 && (
              <Card title="Performance History" size="small" extra={
                <Select value={chartDays} onChange={setChartDays} style={{ width: 120 }}>
                  <Select.Option value={7}>7 Days</Select.Option>
                  <Select.Option value={30}>30 Days</Select.Option>
                  <Select.Option value={90}>90 Days</Select.Option>
                </Select>
              }>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={masterDailyValues}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="value_date" />
                    <YAxis tickFormatter={(val) => `${val.toFixed(1)}%`} />
                    <RechartsTooltip formatter={(val) => `${val.toFixed(2)}%`} />
                    <Legend />
                    <Area type="monotone" dataKey="cumulative_return" name="Portfolio" stroke="#8884d8" fill="#8884d8" fillOpacity={0.3} />
                    <Area type="monotone" dataKey="spy_cumulative_return" name="SPY" stroke="#82ca9d" fill="#82ca9d" fillOpacity={0.3} />
                  </AreaChart>
                </ResponsiveContainer>
              </Card>
            )}

            {/* Positions Table */}
            <Card title="Current Positions" size="small">
              <Table
                columns={positionColumns}
                dataSource={selectedMasterPortfolio.positions || []}
                rowKey="ticker"
                pagination={false}
                size="small"
                scroll={{ x: 1100, y: 400 }}
              />
            </Card>

            {/* Recent Position Changes */}
            {masterPositionChanges.length > 0 && (
              <Card title="Recent Position Changes" size="small">
                <Table
                  columns={positionChangesColumns}
                  dataSource={masterPositionChanges}
                  rowKey="change_id"
                  pagination={{ pageSize: 5 }}
                  size="small"
                />
              </Card>
            )}
          </div>
        )}
      </Modal>
    </div>
  )
}

export default PortfoliosEnhanced
